import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../api';
import type { UserSummary } from '../api';
import { AppShell } from '../ui/AppShell';
import {
  Button,
  Card,
  ErrorBanner,
  Pill,
  Spinner,
  TextInput,
} from '../ui/primitives';

export function UsersAdminPage() {
  const qc = useQueryClient();
  const usersQ = useQuery<UserSummary[]>({
    queryKey: ['users'],
    queryFn: () => api.get<UserSummary[]>('/api/users'),
  });
  const [newUsername, setNewUsername] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [lastResetInfo, setLastResetInfo] = useState<{
    username: string;
    newPassword: string;
  } | null>(null);

  const createMut = useMutation({
    mutationFn: () =>
      api.post<UserSummary>('/api/users', {
        username: newUsername,
        password: newPassword,
      }),
    onSuccess: () => {
      setNewUsername('');
      setNewPassword('');
      setError(null);
      void qc.invalidateQueries({ queryKey: ['users'] });
    },
    onError: (err) =>
      setError(err instanceof ApiError ? err.message : String(err)),
  });

  const deleteMut = useMutation({
    mutationFn: (id: number) => api.delete<void>(`/api/users/${id}`),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['users'] }),
    onError: (err) =>
      setError(err instanceof ApiError ? err.message : String(err)),
  });

  const resetMut = useMutation({
    mutationFn: (u: UserSummary) =>
      api.post<{ newPassword: string }>(`/api/users/${u.id}/reset-password`),
    onSuccess: (res, u) => {
      setLastResetInfo({ username: u.username, newPassword: res.newPassword });
      void qc.invalidateQueries({ queryKey: ['users'] });
    },
    onError: (err) =>
      setError(err instanceof ApiError ? err.message : String(err)),
  });

  return (
    <AppShell title="Users">
      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <h3 className="mb-2 text-small font-semibold text-ink">Add boss user</h3>
          <p className="mb-3 text-caption text-ink-muted">
            Boss users can view every screen but cannot mutate data. Only the
            owner can create / delete / reset boss users.
          </p>
          <form
            className="space-y-3"
            onSubmit={(e) => {
              e.preventDefault();
              setError(null);
              createMut.mutate();
            }}
          >
            <TextInput
              label="Username"
              value={newUsername}
              onChange={(e) => setNewUsername(e.target.value)}
              name="new-user-username"
            />
            <TextInput
              label="Password (≥ 10 chars)"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              name="new-user-password"
            />
            {error && <ErrorBanner>{error}</ErrorBanner>}
            <Button type="submit" disabled={createMut.isPending}>
              {createMut.isPending ? <Spinner /> : 'Create boss user'}
            </Button>
          </form>
        </Card>

        <Card>
          <h3 className="mb-2 text-small font-semibold text-ink">Existing users</h3>
          {usersQ.isLoading ? (
            <div className="flex items-center gap-2 text-caption text-ink-muted">
              <Spinner /> loading…
            </div>
          ) : usersQ.data ? (
            <ul className="space-y-2">
              {usersQ.data.map((u) => (
                <li
                  key={u.id}
                  className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-border p-3 text-small"
                >
                  <div>
                    <span className="font-medium">{u.username}</span>{' '}
                    <Pill tone={u.role === 'owner' ? 'amber' : 'neutral'}>
                      {u.role}
                    </Pill>
                    <span className="ml-2 text-caption text-ink-muted">
                      {u.hasPin ? 'PIN set' : 'no PIN'}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    {u.role !== 'owner' && (
                      <>
                        <Button
                          variant="ghost"
                          onClick={() => {
                            if (confirm(`Reset password for ${u.username}?`))
                              resetMut.mutate(u);
                          }}
                        >
                          Reset password
                        </Button>
                        <Button
                          variant="danger"
                          onClick={() => {
                            if (confirm(`Delete user ${u.username}?`))
                              deleteMut.mutate(u.id);
                          }}
                        >
                          Delete
                        </Button>
                      </>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          ) : null}
        </Card>

        {lastResetInfo && (
          <Card className="lg:col-span-2 border-amber-200 bg-amber-50">
            <h3 className="text-small font-semibold text-amber-700">
              One-time password for {lastResetInfo.username}
            </h3>
            <p className="mt-1 text-caption text-amber-600">
              Share this password securely. Their existing sessions have been
              revoked. They should log in and change their password immediately.
            </p>
            <code className="mt-3 block rounded border border-border bg-surface-raised px-3 py-2 font-mono text-small text-ink">
              {lastResetInfo.newPassword}
            </code>
            <Button
              variant="ghost"
              className="mt-3"
              onClick={() => setLastResetInfo(null)}
            >
              Dismiss
            </Button>
          </Card>
        )}
      </div>
    </AppShell>
  );
}
